// throw-object-range.ts — the pure maths of Throw Object (owner 2026-09-15), shared with
// includes/js-src/janus-engine/port/k3-rules/throw_object.ts.
// The browser previews the range ring and arc from these numbers; Janus repeats them at cast time. Parity is a
// TEST (tools/test/throw-object-range-parity.mjs), never a promise.
//
//   range = 60 × sqrt(base_size / 5) × sqrt(1 − w / W_max) × (1 + pct / 100), nearest 5 ft, min 5 ft
//   W_max = the thrower's Max Carrying Capacity (weight_multiplier × (15 + Body)); heavier ⇒ null (cannot throw)

export const THROW_OBJECT_ABILITY_ID = 'EA8FB8F0-8FAA-4AC0-B939-F038057B50DA';
export const THROW_OBJECT_VARIABLE = 'throw_inventory';
export const THROW_OBJECT_BASE_RANGE_FT = 60;
export const THROW_OBJECT_MIN_RANGE_FT = 5;

/** Improvised impact dice by weight (lb): [maxLb, dice, die]. The last row is open-ended. */
export const THROW_OBJECT_WEIGHT_TIERS: ReadonlyArray<readonly [number, number, number]> = [
    [5, 1, 4],
    [25, 1, 6],
    [100, 1, 8],
    [250, 2, 6],
    [500, 2, 8],
    [1000, 3, 8],
    [Number.POSITIVE_INFINITY, 4, 8],
];

export function throwObjectWeightTier(weightLb: number): { dice: number; die: number; maxLb: number } {
    for (const [maxLb, dice, die] of THROW_OBJECT_WEIGHT_TIERS) {
        if (weightLb <= maxLb) return { dice, die, maxLb };
    }
    const last = THROW_OBJECT_WEIGHT_TIERS[THROW_OBJECT_WEIGHT_TIERS.length - 1]!;
    return { dice: last[1], die: last[2], maxLb: Number.POSITIVE_INFINITY };
}

/** A two-handed heave (the object is one size larger than the thrower) flies this share of the range. */
export const THROW_OBJECT_TWO_HANDED_RANGE_FACTOR = 0.5;

export function throwObjectRangeFt(baseSizeFt: number, capacityMaxLb: number, weightLb: number, distancePct = 0, twoHanded = false): number | null {
    if (!(capacityMaxLb > 0) || weightLb < 0 || weightLb > capacityMaxLb) return null;
    const sizeFactor = Math.sqrt(Math.max(1, baseSizeFt) / 5);
    const loadFactor = Math.sqrt(Math.max(0, 1 - weightLb / capacityMaxLb));
    const multiplier = 1 + Math.max(-90, distancePct) / 100;
    const raw = THROW_OBJECT_BASE_RANGE_FT * sizeFactor * loadFactor * multiplier * (twoHanded ? THROW_OBJECT_TWO_HANDED_RANGE_FACTOR : 1);
    return Math.max(THROW_OBJECT_MIN_RANGE_FT, Math.round(raw / 5) * 5);
}

/** SizeType ids in ascending order (includes/additions.php SizeType::LIST, ordered by base_size). */
export const THROW_OBJECT_SIZE_ORDER: readonly string[] = [
    '13456FEC-A2EE-DB46-5829-0EB2F627E1B7', // tiny
    'AAC1F466-7A8B-C593-1ADC-FC402B8909D7', // small
    'DC23AA88-CB3D-EA4E-F6F7-6389ABC4F9DC', // medium
    '52FFDB16-A991-C8D4-FE59-7C75EE6CF7AD', // large
    'D0EF6D7A-82BC-1E6E-9D36-5004C7E0561C', // huge
    'CEA2946E-D395-46EA-A6E7-74D21EA2376E', // gargantuan
    'C94CDD74-8ACD-26B0-5C53-4CA29CF21D68', // colossal
    '4C33FD22-5382-15DA-1C46-F57F14378AFD', // titanic
];
/** An item with no size layer reads as Tiny (build_inventory_arr's default). */
export const THROW_OBJECT_DEFAULT_ITEM_SIZE = THROW_OBJECT_SIZE_ORDER[0]!;

/**
 * How many size steps the object sits ABOVE its thrower (≤ 0 = the thrower's size or smaller); null when the thrower's
 * size is unknown (the size rule then stands down).
 *   ≤ 0 → any hand · 1 → both hands, half range · ≥ 2 → too big to throw.
 */
export function throwObjectSizeSteps(objectSizeId: string | null | undefined, throwerSizeId: string | null | undefined): number | null {
    const thrower = THROW_OBJECT_SIZE_ORDER.indexOf(String(throwerSizeId ?? '').trim().toUpperCase());
    if (thrower < 0) return null;
    const objectIndex = THROW_OBJECT_SIZE_ORDER.indexOf(String(objectSizeId ?? '').trim().toUpperCase());
    return (objectIndex < 0 ? 0 : objectIndex) - thrower;
}
